// @core/stores/form/FormFormStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { formApi } from '@core/api';
import type {
  AttachFormRequest,
  BatchGetEntityFormsRequest,
  BatchGetEntityFormsResponse,
  DetachFormRequest,
  Form,
  FormLifecycleResponse,
} from '@core/types/form';
import type { PageInfo } from '@core/types/pagination';

class FormFormStore {
  // List state
  items: Form[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: Form | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, Form>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): Form | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: Form | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Attach form to entity
   */
  async AttachForm(target_type: string, target_id: string, data: AttachFormRequest): Promise<FormLifecycleResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await formApi.AttachForm(target_type, target_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Batch get entity forms
   */
  async BatchGetEntityForms(data: BatchGetEntityFormsRequest): Promise<BatchGetEntityFormsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await formApi.BatchGetEntityForms(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Detach form from entity
   */
  async DetachForm(target_type: string, target_id: string, data: DetachFormRequest): Promise<FormLifecycleResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await formApi.DetachForm(target_type, target_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get entity form
   */
  async GetEntityForm(target_type: string, target_id: string): Promise<Form> {
    // Check cache first
    const cached = this.cache.get(target_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await formApi.GetEntityForm(target_type, target_id);
      const item = response.form;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }
}

export const formFormStore = new FormFormStore();
